import { jsPDF } from 'jspdf';
import {
  AllergenStatus,
  DietaryAttributeStatus,
  RecipeV2Detail,
} from '../../../app/services/api/recipeV2';

export type RecipePublishStyle = 'print' | 'full';

export interface RecipePublishOptions {
  style: RecipePublishStyle;
  generatedAt?: Date;
  title?: string;
  fileNamePrefix?: string;
}

interface NormalizedRecipePublishOptions {
  style: RecipePublishStyle;
  generatedAt: Date;
  title?: string;
  fileNamePrefix?: string;
}

interface PublishRecipeModel {
  id: number;
  itemNumber: string;
  name: string;
  description?: string | null;
  category: string;
  yieldText: string;
  ingredients: Array<{ name: string; quantity: number; uom: string }>;
  steps: Array<{
    number: number;
    title: string;
    instruction: string;
    meta: string[];
  }>;
  allergens: Array<{ name: string; status: string }>;
  dietary: Array<{ name: string; status: string }>;
  nutrition: Array<{ name: string; value: string }>;
  imageUrl?: string | null;
  versionLabel: string;
  publicationLabel: string;
  operationalStatusLabel: string;
  effectiveWindowLabel: string;
  productionMethodLabel?: string | null;
  complianceLabel: string;
}

const PAGE_MARGIN = 14;
const PAGE_BOTTOM = 18;
const LINE_HEIGHT = 5;
const CONTENT_WIDTH = 182;

const safeFileName = (value: string): string =>
  value
    .trim()
    .replace(/[^a-zA-Z0-9-_ ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .toLowerCase() || 'recipe';

const yieldText = (recipe: RecipeV2Detail): string => {
  if (!recipe.yields?.length) return 'Yield not recorded';
  return recipe.yields
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((value) => `${value.quantity} ${value.uom}`)
    .join(' / ');
};

export const buildRecipePublishModel = (recipe: RecipeV2Detail): PublishRecipeModel => ({
  id: recipe.id,
  itemNumber: recipe.itemNumber,
  name: recipe.description,
  description: recipe.narrativeDescription,
  category: recipe.primaryCategory,
  yieldText: yieldText(recipe),
  ingredients: (recipe.components ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter((component) => component.componentRole !== 'Packaging' && component.componentRole !== 'Operational')
    .map((component) => ({
      name: component.componentDescription,
      quantity: component.quantity,
      uom: component.uom,
    })),
  steps: (recipe.steps ?? [])
    .slice()
    .sort((a, b) => a.stepNumber - b.stepNumber)
    .map((step) => ({
      number: step.stepNumber,
      title: step.title,
      instruction: step.instruction,
      meta: [
        step.durationMinutes != null ? `${step.durationMinutes} min` : '',
        step.temperature ?? '',
        ...(step.equipment ?? []).map((item) => item.name),
      ].filter(Boolean),
    })),
  allergens: (recipe.allergenSummary ?? [])
    .filter((allergen) =>
      allergen.status === AllergenStatus.Contains
      || allergen.status === AllergenStatus.MayContain
      || allergen.status === AllergenStatus.Unknown)
    .map((allergen) => ({ name: allergen.allergenName, status: allergen.status })),
  dietary: (recipe.dietaryAttributes ?? [])
    .filter((attribute) =>
      attribute.status === DietaryAttributeStatus.Suitable
      || attribute.status === DietaryAttributeStatus.Certified
      || attribute.status === DietaryAttributeStatus.NotSuitable)
    .map((attribute) => ({ name: attribute.name, status: attribute.status })),
  nutrition: (recipe.nutrition ?? [])
    .filter((nutrient) => nutrient.value != null || nutrient.perServing != null)
    .map((nutrient) => ({
      name: nutrient.name,
      value: nutrient.perServing != null
        ? `${nutrient.perServing} ${nutrient.valueUom ?? ''} per serving`.trim()
        : `${nutrient.value} ${nutrient.valueUom ?? ''}`.trim(),
    })),
  imageUrl: recipe.mediaUrl,
  versionLabel: recipe.recipeVersionId != null
    ? `Recipe version ${recipe.currentVersion ?? recipe.recipeVersionId} (ID ${recipe.recipeVersionId})`
    : 'Legacy governed recipe',
  publicationLabel: recipe.recipePublicationId != null
    ? `Publication ID ${recipe.recipePublicationId}`
    : 'Legacy/default publication',
  operationalStatusLabel: `Operational status: ${recipe.status}`,
  effectiveWindowLabel: `Effective: ${recipe.effectiveFrom ?? 'open'} to ${recipe.effectiveTo ?? 'open'}`,
  productionMethodLabel: recipe.productionMethod?.name
    ?? (recipe.productionMethodId != null ? `Production method ID ${recipe.productionMethodId}` : null),
  complianceLabel: recipe.complianceRevisionIds?.length
    ? `Compliance revision(s): ${recipe.complianceRevisionIds.join(', ')}`
    : 'Compliance revision: legacy/default governed state',
});

export const publishingImageFormat = (dataUrl: string): 'PNG' | 'WEBP' | 'JPEG' => {
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return 'JPEG';
};

const loadImageDataUrl = async (url?: string | null): Promise<string | null> => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const ensureSpace = (doc: jsPDF, y: number, height = LINE_HEIGHT): number => {
  if (y + height > doc.internal.pageSize.getHeight() - PAGE_BOTTOM) {
    doc.addPage();
    return PAGE_MARGIN;
  }
  return y;
};

const writeWrapped = (
  doc: jsPDF,
  text: string,
  y: number,
  options: { size?: number; bold?: boolean; maxWidth?: number } = {},
): number => {
  const size = options.size ?? 10;
  const maxWidth = options.maxWidth ?? CONTENT_WIDTH;
  doc.setFont('helvetica', options.bold ? 'bold' : 'normal');
  doc.setFontSize(size);
  const lines = doc.splitTextToSize(text || '', maxWidth) as string[];
  let cursor = y;
  lines.forEach((line) => {
    cursor = ensureSpace(doc, cursor, LINE_HEIGHT);
    doc.text(line, PAGE_MARGIN, cursor);
    cursor += LINE_HEIGHT;
  });
  return cursor;
};

const heading = (doc: jsPDF, text: string, y: number, style: RecipePublishStyle): number => {
  const cursor = ensureSpace(doc, y + 2, 9);
  if (style === 'full') {
    doc.setFillColor(36, 87, 64);
    doc.rect(PAGE_MARGIN, cursor - 5, CONTENT_WIDTH, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(text, PAGE_MARGIN + 2, cursor);
    doc.setTextColor(0, 0, 0);
    return cursor + 6;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(text, PAGE_MARGIN, cursor);
  doc.setDrawColor(80);
  doc.line(PAGE_MARGIN, cursor + 1, PAGE_MARGIN + CONTENT_WIDTH, cursor + 1);
  return cursor + 6;
};

const addTraceability = (
  doc: jsPDF,
  model: PublishRecipeModel,
  generatedAt: Date,
  y: number,
): number => {
  let cursor = heading(doc, 'Traceability', y, 'print');
  doc.setFontSize(8);
  cursor = writeWrapped(doc, model.versionLabel, cursor, { size: 8 });
  cursor = writeWrapped(doc, model.publicationLabel, cursor, { size: 8 });
  cursor = writeWrapped(doc, model.operationalStatusLabel, cursor, { size: 8 });
  cursor = writeWrapped(doc, model.effectiveWindowLabel, cursor, { size: 8 });
  cursor = writeWrapped(doc, model.complianceLabel, cursor, { size: 8 });
  cursor = writeWrapped(doc, `Generated ${generatedAt.toISOString()}`, cursor, { size: 8 });
  return cursor;
};

const renderRecipe = async (
  doc: jsPDF,
  recipe: RecipeV2Detail,
  options: NormalizedRecipePublishOptions,
  startOnNewPage: boolean,
): Promise<void> => {
  if (startOnNewPage) doc.addPage();
  const model = buildRecipePublishModel(recipe);
  let y = PAGE_MARGIN;

  if (options.style === 'full') {
    doc.setFillColor(245, 248, 246);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 42, 'F');
  }

  y = writeWrapped(doc, model.name, y + 4, { size: 20, bold: true });
  y = writeWrapped(doc, `${model.itemNumber} | ${model.category} | ${model.yieldText}`, y + 1, { size: 9 });
  if (model.productionMethodLabel) {
    y = writeWrapped(doc, model.productionMethodLabel, y, { size: 9 });
  }
  if (model.description) y = writeWrapped(doc, model.description, y + 2, { size: 10 });

  if (options.style === 'full') {
    const imageData = await loadImageDataUrl(model.imageUrl);
    if (imageData) {
      y = ensureSpace(doc, y + 4, 55);
      try {
        doc.addImage(
          imageData,
          publishingImageFormat(imageData),
          PAGE_MARGIN,
          y,
          75,
          50,
          undefined,
          'FAST',
        );
        y += 55;
      } catch {
        // Image format/CORS differences must not prevent an operational artifact.
      }
    }
  }

  y = heading(doc, 'Ingredients', y + 2, options.style);
  if (!model.ingredients.length) {
    y = writeWrapped(doc, 'No ingredients recorded.', y);
  } else {
    model.ingredients.forEach((ingredient) => {
      y = writeWrapped(doc, `${ingredient.quantity} ${ingredient.uom}  ${ingredient.name}`, y);
    });
  }

  y = heading(doc, 'Allergens & dietary', y + 2, options.style);
  const allergenText = model.allergens.length
    ? model.allergens.map((item) => `${item.name}: ${item.status}`).join(' | ')
    : 'No declared Contains/May Contain/Unknown allergens.';
  y = writeWrapped(doc, allergenText, y);
  if (model.dietary.length) {
    y = writeWrapped(doc, model.dietary.map((item) => `${item.name}: ${item.status}`).join(' | '), y + 1);
  }

  if (model.nutrition.length) {
    y = heading(doc, 'Nutrition', y + 2, options.style);
    model.nutrition.forEach((item) => {
      y = writeWrapped(doc, `${item.name}: ${item.value}`, y);
    });
  }

  y = heading(doc, 'Method', y + 2, options.style);
  if (!model.steps.length) {
    y = writeWrapped(doc, 'No method steps recorded.', y);
  } else {
    model.steps.forEach((step) => {
      y = ensureSpace(doc, y + 1, 15);
      y = writeWrapped(doc, `${step.number}. ${step.title || `Step ${step.number}`}`, y, { bold: true });
      y = writeWrapped(doc, step.instruction, y);
      if (step.meta.length) y = writeWrapped(doc, step.meta.join(' | '), y, { size: 8 });
      y += 2;
    });
  }

  addTraceability(doc, model, options.generatedAt, y + 2);
};

const normalizeOptions = (options: RecipePublishOptions): NormalizedRecipePublishOptions => ({
  style: options.style,
  generatedAt: options.generatedAt ?? new Date(),
  title: options.title,
  fileNamePrefix: options.fileNamePrefix,
});

export const saveRecipePdf = async (
  recipe: RecipeV2Detail,
  options: RecipePublishOptions,
): Promise<void> => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  await renderRecipe(doc, recipe, normalizeOptions(options), false);
  doc.save(`${safeFileName(recipe.description)}-${options.style === 'print' ? 'print' : 'colour'}.pdf`);
};

export const saveRecipeBookPdf = async (
  recipes: RecipeV2Detail[],
  options: RecipePublishOptions,
): Promise<void> => {
  const normalized = normalizeOptions(options);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.text(normalized.title ?? 'Recipe Book', PAGE_MARGIN, 30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(`${recipes.length} governed recipes`, PAGE_MARGIN, 39);
  doc.text(`Generated ${normalized.generatedAt.toISOString()}`, PAGE_MARGIN, 46);

  for (const recipe of recipes) {
    await renderRecipe(doc, recipe, normalized, true);
  }

  const filePrefix = safeFileName(normalized.fileNamePrefix ?? normalized.title ?? 'recipe-book');
  doc.save(`${filePrefix}-${normalized.generatedAt.toISOString().slice(0, 10)}-${normalized.style}.pdf`);
};

export type MatrixColumn = {
  key: string;
  label: string;
  marker: (recipe: RecipeV2Detail) => string;
};

export const buildMatrixColumns = (recipes: RecipeV2Detail[]): MatrixColumn[] => {
  const allergenColumns = new Map<string, MatrixColumn>();
  const dietaryColumns = new Map<string, MatrixColumn>();

  recipes.forEach((recipe) => {
    (recipe.allergenSummary ?? []).forEach((allergen) => {
      if (allergenColumns.has(allergen.allergenCode)) return;
      allergenColumns.set(allergen.allergenCode, {
        key: `allergen:${allergen.allergenCode}`,
        label: allergen.allergenCode,
        marker: (value) => {
          const status = value.allergenSummary?.find((item) => item.allergenCode === allergen.allergenCode)?.status
            ?? AllergenStatus.Unknown;
          if (status === AllergenStatus.Contains) return 'C';
          if (status === AllergenStatus.MayContain) return 'M';
          if (status === AllergenStatus.FreeFrom) return 'F';
          if (status === AllergenStatus.NotPresent) return '-';
          return '?';
        },
      });
    });

    (recipe.dietaryAttributes ?? []).forEach((attribute) => {
      if (dietaryColumns.has(attribute.code)) return;
      dietaryColumns.set(attribute.code, {
        key: `dietary:${attribute.code}`,
        label: attribute.code,
        marker: (value) => {
          const status = value.dietaryAttributes?.find((item) => item.code === attribute.code)?.status;
          if (status === DietaryAttributeStatus.Certified) return '*';
          if (status === DietaryAttributeStatus.Suitable) return 'Y';
          if (status === DietaryAttributeStatus.NotSuitable) return 'N';
          if (status === DietaryAttributeStatus.Contains) return 'C';
          if (status === DietaryAttributeStatus.MayContain) return 'M';
          if (status === DietaryAttributeStatus.FreeFrom) return 'F';
          return '?';
        },
      });
    });
  });

  return [
    ...Array.from(allergenColumns.values()).sort((a, b) => a.label.localeCompare(b.label)),
    ...Array.from(dietaryColumns.values()).sort((a, b) => a.label.localeCompare(b.label)),
  ];
};

export const buildMatrixRecipeProvenance = (recipe: RecipeV2Detail): string => {
  const versionRef = recipe.currentVersion ?? recipe.recipeVersionId ?? '?';
  const publicationRef = recipe.recipePublicationId ?? '?';
  const complianceRef = recipe.complianceRevisionIds?.length
    ? recipe.complianceRevisionIds.join(',')
    : '?';
  return `${recipe.itemNumber} | v:${versionRef} | pub:${publicationRef} | comp:${complianceRef}`;
};

export const saveAllergenMatrixPdf = (
  recipes: RecipeV2Detail[],
  options: RecipePublishOptions,
): void => {
  const normalized = normalizeOptions(options);
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const columns = buildMatrixColumns(recipes);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const recipeWidth = 92;
  const preferredCellWidth = 14;
  const maxColumnsPerPage = Math.max(
    1,
    Math.floor((pageWidth - (PAGE_MARGIN * 2) - recipeWidth) / preferredCellWidth),
  );
  const columnGroups: MatrixColumn[][] = [];
  for (let index = 0; index < columns.length; index += maxColumnsPerPage) {
    columnGroups.push(columns.slice(index, index + maxColumnsPerPage));
  }
  if (columnGroups.length === 0) columnGroups.push([]);

  columnGroups.forEach((group, groupIndex) => {
    if (groupIndex > 0) doc.addPage();

    const cellWidth = group.length > 0
      ? Math.min(22, (pageWidth - (PAGE_MARGIN * 2) - recipeWidth) / group.length)
      : preferredCellWidth;
    let y = 16;

    const drawTitleAndHeader = () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(normalized.title ?? 'Recipe Allergen & Dietary Matrix', PAGE_MARGIN, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(
        `Generated ${normalized.generatedAt.toISOString()} | ${recipes.length} governed recipes | columns ${groupIndex + 1}/${columnGroups.length}`,
        PAGE_MARGIN,
        y + 6,
      );
      y += 14;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text('Recipe', PAGE_MARGIN, y);
      group.forEach((column, columnIndex) => {
        doc.text(column.label.slice(0, 9), PAGE_MARGIN + recipeWidth + (columnIndex * cellWidth), y);
      });
      y += 5;
      doc.line(PAGE_MARGIN, y - 2, pageWidth - PAGE_MARGIN, y - 2);
    };

    drawTitleAndHeader();

    recipes.forEach((recipe) => {
      if (y > pageHeight - 22) {
        doc.addPage();
        y = 16;
        drawTitleAndHeader();
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(recipe.description.slice(0, 44), PAGE_MARGIN, y);
      doc.setFontSize(5.5);
      const provenance = buildMatrixRecipeProvenance(recipe);
      doc.text(provenance.slice(0, 82), PAGE_MARGIN, y + 3);
      group.forEach((column, columnIndex) => {
        const matrixMarker = column.marker(recipe);
        if (normalized.style === 'full') {
          if (matrixMarker === 'C' || matrixMarker === 'N') doc.setTextColor(180, 30, 30);
          else if (matrixMarker === 'M') doc.setTextColor(190, 105, 0);
          else if (matrixMarker === 'F' || matrixMarker === 'Y' || matrixMarker === '*') doc.setTextColor(20, 120, 70);
          else doc.setTextColor(40, 40, 40);
        }
        doc.text(matrixMarker, PAGE_MARGIN + recipeWidth + (columnIndex * cellWidth), y + 1);
        doc.setTextColor(0, 0, 0);
      });
      y += 8;
    });

    y += 4;
    const legend = 'Allergens: C = Contains, M = May Contain, F = Free From, - = Not Present, ? = Unknown. Dietary: * = Certified, Y = Suitable, N = Not Suitable, C = Contains, M = May Contain, F = Free From, ? = Unknown.';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const legendLines = doc.splitTextToSize(legend, pageWidth - (PAGE_MARGIN * 2)) as string[];
    legendLines.forEach((line) => {
      if (y > pageHeight - PAGE_BOTTOM) {
        doc.addPage();
        y = PAGE_MARGIN;
      }
      doc.text(line, PAGE_MARGIN, y);
      y += 4;
    });
  });

  const filePrefix = safeFileName(normalized.fileNamePrefix ?? normalized.title ?? 'recipe-allergen-dietary-matrix');
  doc.save(`${filePrefix}-${normalized.generatedAt.toISOString().slice(0, 10)}-${normalized.style}.pdf`);
};
